// /app/frontend/src/components/configurator/MaterialCompareModal.jsx
// Modal for comparing multiple materials side by side

import React from 'react';
import { Scale, Package } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { brandLabel } from '../../lib/brandNames';

const MaterialCompareModal = ({
  open,
  onOpenChange,
  materialsToCompare,
  setMaterialsToCompare,
  toggleMaterialComparison,
  onRequestSamples
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Compare Materials ({materialsToCompare.length})
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {materialsToCompare.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 px-3 text-sm font-medium text-neutral-500">Property</th>
                    {materialsToCompare.map(mat => (
                      <th key={mat.id} className="text-left py-2 px-3 text-sm font-medium text-neutral-900">
                        {mat.name}
                        <button
                          onClick={() => toggleMaterialComparison(mat, {id: mat.brand_id}, {id: mat.collection_id})}
                          className="ml-2 text-neutral-400 hover:text-red-500"
                          title="Remove from comparison"
                        >
                          ×
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-neutral-100">
                    <td className="py-3 px-3 text-sm text-neutral-500">Brand</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3 text-sm">{brandLabel(mat.brand_name)}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-neutral-100">
                    <td className="py-3 px-3 text-sm text-neutral-500">Collection</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3 text-sm">{mat.collection_name}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-neutral-100">
                    <td className="py-3 px-3 text-sm text-neutral-500">Composition</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3 text-sm">{mat.composition || '-'}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-neutral-100">
                    <td className="py-3 px-3 text-sm text-neutral-500">Weight</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3 text-sm">{mat.weight || '-'}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-neutral-100">
                    <td className="py-3 px-3 text-sm text-neutral-500">Base Price</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3 text-sm font-medium text-green-600">
                        {mat.base_price ? `$${mat.base_price}/sq ft` : '-'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-neutral-100">
                    <td className="py-3 px-3 text-sm text-neutral-500">Colors Available</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3 text-sm">{mat.colors?.length || 0} colors</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-3 px-3 text-sm text-neutral-500">Color Swatches</td>
                    {materialsToCompare.map(mat => (
                      <td key={mat.id} className="py-3 px-3">
                        <div className="flex gap-1">
                          {mat.colors?.slice(0, 5).map(color => (
                            <div
                              key={color.id}
                              className="w-6 h-6 rounded border border-neutral-200"
                              style={{ backgroundColor: color.hex_color || '#ccc' }}
                              title={color.name}
                            />
                          ))}
                          {(mat.colors?.length || 0) > 5 && (
                            <span className="text-xs text-neutral-400 self-center">+{mat.colors.length - 5}</span>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-8 text-neutral-500">
              Select materials to compare by clicking the + button on each material.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setMaterialsToCompare([])}>Clear All</Button>
          <Button 
            onClick={onRequestSamples}
            className="bg-amber-600 hover:bg-amber-700"
            disabled={materialsToCompare.length === 0}
          >
            <Package className="w-4 h-4 mr-2" />
            Request Samples
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialCompareModal;
